/**
 * PURPOSE: Vista Calendario / Timeline del Centro de Programación (FTR-0028 WG3, §18 / vista 3).
 * RESPONSIBILITIES: Ver la carga temporal de un día real: solapamientos, huecos, ocupación por
 *   cancha y sede, y los partidos que aún no tienen sitio.
 *
 *   NO ES UN GOOGLE CALENDAR (§11 del encargo). Se prioriza densidad y operación: una rejilla CSS de
 *   horas por canchas, un día a la vez, y la edición por el panel lateral del partido seleccionado.
 *   NO se implementa drag & drop: mover un bloque arrastrándolo es una escritura que necesita
 *   confirmación del backend (sede, cancha, propiedad, actividad) y un arrastre que a veces "rebota"
 *   es peor que un campo que siempre dice la verdad. La edición directa de fecha/hora/cancha cubre
 *   el mismo caso y nunca miente sobre el resultado.
 *
 *   LOS CONFLICTOS LOS CALCULA EL BACKEND (`engine/timelineConflicts.js`) y llegan en `GET /draft`.
 *   Recalcularlos aquí crearía una segunda verdad que podría discrepar de la que protege los datos.
 *
 *   EL BYE NUNCA APARECE (D-20): no ocupa cancha ni horario porque no se juega.
 * COLLABORATORS: ProgrammingCenterProvider.jsx, MatchRow.jsx, utils/programmingCenterGrouping.js.
 * ROUTE: winscore/src/area/FasesArea/ProgrammingCenter/views/TimelineView.jsx
 */
import React, { useMemo, useState } from "react";
import { FiAlertTriangle, FiClock, FiInbox } from "react-icons/fi";

import { useProgrammingCenter } from "../ProgrammingCenterProvider.jsx";
import {
  buildTimeline,
  conflictSeverity,
  minutesToTime,
} from "../../../../utils/programmingCenterGrouping.js";
import { assignableCourts } from "../../../../utils/programmingCenterEdit.js";
import { displayMatchKey } from "../../../../utils/programmingCenterModel.js";
import MatchRow from "../components/MatchRow.jsx";

const ROW_HEIGHT = 34;

const SlotEditor = ({ row, onClose }) => {
  const { context, scheduleDraft, setScheduleDraft, actions, busy, courtsByVenue } =
    useProgrammingCenter();
  const entry = scheduleDraft[row.iddraftmatch] || {};
  const venues = context?.venues || [];
  const courts = entry.idsede ? assignableCourts(context, entry.idsede) : [];

  const change = (field, value) =>
    setScheduleDraft((current) => actions.changeScheduleField(current, row, field, value));

  const inputClass =
    "w-full rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm font-semibold text-slate-800 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100";

  return (
    <aside className="flex w-72 shrink-0 flex-col gap-3 rounded-2xl border border-blue-200 bg-blue-50/50 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-black text-slate-900">{row.label}</p>
          <p className="text-[11px] font-bold text-slate-500" title={displayMatchKey(row).title}>
            {row.groupLabel || row.tagFinal || "—"} · {displayMatchKey(row).text}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-slate-200 bg-white px-2 py-0.5 text-xs font-black text-slate-500 hover:text-slate-800"
        >
          Cerrar
        </button>
      </div>

      <label className="flex flex-col gap-0.5">
        <span className="text-[10px] font-black uppercase tracking-wide text-slate-400">Fecha</span>
        <input
          type="date"
          value={entry.date ?? ""}
          onChange={(event) => change("date", event.target.value)}
          className={inputClass}
        />
      </label>
      <label className="flex flex-col gap-0.5">
        <span className="text-[10px] font-black uppercase tracking-wide text-slate-400">Hora</span>
        <input
          type="time"
          value={entry.time ?? ""}
          onChange={(event) => change("time", event.target.value)}
          className={inputClass}
        />
      </label>
      <label className="flex flex-col gap-0.5">
        <span className="text-[10px] font-black uppercase tracking-wide text-slate-400">Sede</span>
        <select
          value={entry.idsede ?? ""}
          onChange={(event) => change("idsede", event.target.value)}
          className={inputClass}
        >
          <option value="">Sin sede</option>
          {venues.map((venue) => (
            <option key={venue.idsede} value={venue.idsede}>
              {venue.name}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-0.5">
        <span className="text-[10px] font-black uppercase tracking-wide text-slate-400">Cancha</span>
        <select
          value={entry.idcancha ?? ""}
          onChange={(event) => change("idcancha", event.target.value)}
          disabled={!entry.idsede}
          className={`${inputClass} disabled:bg-slate-50 disabled:text-slate-400`}
        >
          <option value="">Sin cancha</option>
          {courts.map((court) => (
            <option key={court.idcancha} value={court.idcancha}>
              {court.name}
            </option>
          ))}
        </select>
      </label>

      <button
        type="button"
        onClick={() => actions.saveInlineChanges()}
        disabled={Boolean(busy)}
        className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-black text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Guardar cambios
      </button>
      <p className="text-[11px] font-bold text-slate-500">
        Todo cambio se escribe en el servidor: si una sede, una cancha o la propiedad del partido lo
        impiden, se rechaza y la vista vuelve al estado real.
      </p>
      {courtsByVenue.size === 0 ? (
        <p className="text-[11px] font-bold text-amber-700">
          Este evento no tiene canchas registradas: los partidos comparten carril por sede.
        </p>
      ) : null}
    </aside>
  );
};

const TimelineView = () => {
  const { visibleRows, draft, conflictsByKey, timelineDate, setTimelineDate } =
    useProgrammingCenter();
  const [focused, setFocused] = useState(null);

  const slotMinutes = Number(draft?.timeline?.slotMinutes) || Number(draft?.draft?.slot_minutes) || 60;
  const timeline = useMemo(
    () => buildTimeline(visibleRows, { slotMinutes, date: timelineDate }),
    [visibleRows, slotMinutes, timelineDate]
  );

  const gaps = (draft?.timeline?.gaps || []).filter(
    (gap) => !timeline.activeDate || gap.date === timeline.activeDate
  );
  const conflicts = (draft?.timeline?.conflicts || []).filter(
    (conflict) => !timeline.activeDate || conflict.date === timeline.activeDate
  );

  const focusedRow = focused ? visibleRows.find((row) => row.iddraftmatch === focused) : null;

  if (!timeline.days.length && !timeline.unscheduled.length) {
    return (
      <p className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm font-bold text-slate-500">
        No hay partidos que coincidan con los filtros actuales.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <FiClock className="text-sm text-slate-400" />
          <span className="text-[11px] font-black uppercase tracking-wide text-slate-500">Día</span>
          {timeline.days.length ? (
            timeline.days.map((day) => (
              <button
                key={day}
                type="button"
                onClick={() => setTimelineDate(day)}
                className={`rounded-full border px-3 py-1 text-xs font-black transition ${
                  day === timeline.activeDate
                    ? "border-blue-500 bg-blue-600 text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:border-blue-300"
                }`}
              >
                {day}
              </button>
            ))
          ) : (
            <span className="text-xs font-bold text-slate-500">Ningún partido tiene fecha aún.</span>
          )}
        </div>
        <div className="flex items-center gap-2 text-[11px] font-black">
          <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-1 text-rose-700">
            {conflicts.filter((item) => item.severity === "error").length} cruces de cancha
          </span>
          <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-amber-700">
            {conflicts.filter((item) => item.severity === "warning").length} cruces de sede
          </span>
          <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-slate-600">
            {gaps.length} huecos
          </span>
          {/* FTR-0028 WG7: los solapes crecen con el cuadrado de los partidos que comparten fecha y
              sede, así que el servidor devuelve una muestra y el recuento completo. Decirlo es
              obligatorio: sin este aviso, las filas fuera de la muestra parecerían sin conflicto. */}
          {draft?.timeline?.summary?.truncated ? (
            <span
              className="rounded-full border border-rose-300 bg-rose-100 px-2 py-1 text-rose-800"
              title={`Hay ${draft.timeline.summary.conflicts} solapes en total. Se muestran los primeros ${draft.timeline.summary.returned}: reparte horas o sedes y vuelve a mirar.`}
            >
              {draft.timeline.summary.conflicts} solapes en total · se muestran {draft.timeline.summary.returned}
            </span>
          ) : null}
        </div>
      </div>

      <div className="flex gap-3">
        <div className="min-w-0 flex-1 overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          {timeline.lanes.length ? (
            <div
              className="grid min-w-[640px]"
              style={{
                gridTemplateColumns: `72px repeat(${timeline.lanes.length}, minmax(140px, 1fr))`,
              }}
            >
              <div className="sticky left-0 z-10 border-b border-r border-slate-200 bg-slate-50 px-2 py-2 text-[10px] font-black uppercase tracking-wide text-slate-400">
                Hora
              </div>
              {timeline.lanes.map((lane) => (
                <div
                  key={lane.key}
                  className="border-b border-r border-slate-200 bg-slate-50 px-2 py-2 last:border-r-0"
                >
                  <p className="truncate text-[11px] font-black text-slate-700" title={lane.venueName}>
                    {lane.venueName}
                  </p>
                  <p className="truncate text-[10px] font-bold uppercase tracking-wide text-slate-400">
                    {lane.courtName}
                  </p>
                </div>
              ))}

              {timeline.slots.map((minute) => (
                <React.Fragment key={minute}>
                  <div
                    className="sticky left-0 z-10 border-b border-r border-slate-100 bg-white px-2 text-[10px] font-black text-slate-400"
                    style={{ height: ROW_HEIGHT, lineHeight: `${ROW_HEIGHT}px` }}
                  >
                    {minutesToTime(minute)}
                  </div>
                  {timeline.lanes.map((lane) => {
                    const blocks = timeline.placement.filter(
                      (item) =>
                        item.laneKey === lane.key &&
                        item.start >= minute &&
                        item.start < minute + timeline.stepMinutes
                    );
                    return (
                      <div
                        key={`${lane.key}-${minute}`}
                        className="relative border-b border-r border-slate-100 p-0.5 last:border-r-0"
                        style={{ height: ROW_HEIGHT }}
                      >
                        {blocks.map((block) => {
                          const rowConflicts = conflictsByKey.get(block.row.draftKey) || [];
                          return (
                            <button
                              key={block.row.key}
                              type="button"
                              onClick={() => setFocused(block.row.iddraftmatch)}
                              className="absolute inset-x-0.5 z-10 text-left"
                              style={{
                                top: 2,
                                height:
                                  (timeline.slotMinutes / timeline.stepMinutes) * ROW_HEIGHT - 4,
                              }}
                            >
                              <MatchRow
                                row={block.row}
                                layout="calendar"
                                conflicts={rowConflicts}
                                severity={conflictSeverity(rowConflicts)}
                              />
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          ) : (
            <p className="p-8 text-center text-sm font-bold text-slate-500">
              Ningún partido de este día tiene hora y sede asignadas todavía.
            </p>
          )}
        </div>

        {focusedRow ? <SlotEditor row={focusedRow} onClose={() => setFocused(null)} /> : null}
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <section className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
          <p className="mb-2 flex items-center gap-2 text-[11px] font-black uppercase tracking-wide text-slate-500">
            <FiInbox className="text-xs" />
            Sin programar ({timeline.unscheduled.length})
          </p>
          {timeline.unscheduled.length ? (
            <ul className="flex max-h-56 flex-col gap-1 overflow-y-auto">
              {timeline.unscheduled.map((row) => (
                <li key={row.key}>
                  <button
                    type="button"
                    onClick={() => setFocused(row.iddraftmatch)}
                    className="flex w-full items-center justify-between gap-2 rounded-lg border border-slate-200 px-2 py-1.5 text-left transition hover:border-blue-300 hover:bg-blue-50"
                  >
                    <span className="truncate text-xs font-black text-slate-800">{row.label}</span>
                    <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                      {row.groupLabel || row.tagFinal || "—"}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs font-bold text-slate-500">
              Todos los partidos visibles tienen día, hora y sede.
            </p>
          )}
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
          <p className="mb-2 flex items-center gap-2 text-[11px] font-black uppercase tracking-wide text-slate-500">
            <FiAlertTriangle className="text-xs" />
            Conflictos y huecos
          </p>
          {conflicts.length || gaps.length ? (
            <ul className="flex max-h-56 flex-col gap-1 overflow-y-auto text-xs font-bold">
              {conflicts.map((conflict, position) => (
                <li
                  key={`conflict-${position}`}
                  className={`rounded-lg border px-2 py-1.5 ${
                    conflict.severity === "error"
                      ? "border-rose-200 bg-rose-50 text-rose-700"
                      : "border-amber-200 bg-amber-50 text-amber-700"
                  }`}
                >
                  {conflict.severity === "error" ? "Misma cancha" : "Misma sede"} ·{" "}
                  {conflict.rows.map((item) => item.draft_key).join(" ↔ ")} ·{" "}
                  {conflict.overlapMinutes} min
                </li>
              ))}
              {gaps.map((gap, position) => (
                <li
                  key={`gap-${position}`}
                  className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-slate-600"
                >
                  Hueco de {gap.minutes} min entre {gap.from} y {gap.to}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs font-bold text-slate-500">
              Sin solapamientos ni huecos relevantes en este día.
            </p>
          )}
        </section>
      </div>
    </div>
  );
};

export default TimelineView;
